import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { kitchenItemsApi, type KitchenItem } from '../lib/api/accounting';
import { getCompanyId } from '../lib/config';
import { formatCurrency } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '../components/ui/form';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { MoonLoader } from '../components/ui/moon-loader';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useToast } from '../hooks/use-toast';
import { Plus, Search, Edit, Trash2, FileText, Download, FileSpreadsheet, FileDown, Package, PackageX } from 'lucide-react';
import { format } from 'date-fns';
import { TranslatedText } from '../components/translated-text';
import { useTranslation } from '../hooks/useTranslation';

const kitchenItemSchema = z.object({
  poNumber: z.string().min(1, 'PO Number is required'),
  description: z.string().min(1, 'Description is required'),
  amount: z.coerce.number().nonnegative('Amount must be non-negative'),
  date: z.string().min(1, 'Date is required'),
  status: z.enum(['in_stock', 'out_of_stock']).optional(),
});

type KitchenItemFormData = z.infer<typeof kitchenItemSchema>;

export function KitchenItemsTab() {
  const { toast } = useToast();
  const { tSync } = useTranslation();
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState('');
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isReportOpen, setIsReportOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<KitchenItem | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [reportStartDate, setReportStartDate] = useState(new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0]);
  const [reportEndDate, setReportEndDate] = useState(new Date().toISOString().split('T')[0]);

  const form = useForm<KitchenItemFormData>({
    resolver: zodResolver(kitchenItemSchema),
    defaultValues: {
      poNumber: '',
      description: '',
      amount: 0,
      date: new Date().toISOString().split('T')[0],
      status: 'in_stock',
    },
  });

  // Fetch kitchen items
  const { data: kitchenItemsData, isLoading } = useQuery({
    queryKey: ['kitchen-items', currentPage, searchTerm],
    queryFn: async () => {
      return await kitchenItemsApi.getKitchenItems(
        getCompanyId(),
        currentPage,
        pageSize,
        searchTerm || undefined
      );
    },
  });

  const kitchenItems = kitchenItemsData?.items || [];
  const pagination = kitchenItemsData?.pagination;

  // Create mutation
  const createMutation = useMutation({
    mutationFn: (data: KitchenItemFormData) => kitchenItemsApi.createKitchenItem(data as any),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['kitchen-items'] });
      await queryClient.refetchQueries({ queryKey: ['kitchen-items'] });
      setIsCreateOpen(false);
      form.reset();
      toast({
        title: 'Success',
        description: 'Kitchen item created successfully',
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: error?.message || 'Failed to create kitchen item',
        variant: 'destructive',
      });
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: KitchenItemFormData }) =>
      kitchenItemsApi.updateKitchenItem(id, data as any),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['kitchen-items'] });
      await queryClient.refetchQueries({ queryKey: ['kitchen-items'] });
      setIsCreateOpen(false);
      setEditingItem(null);
      form.reset();
      toast({
        title: 'Success',
        description: 'Kitchen item updated successfully',
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: error?.message || 'Failed to update kitchen item',
        variant: 'destructive',
      });
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (id: string) => kitchenItemsApi.deleteKitchenItem(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['kitchen-items'] });
      toast({
        title: 'Success',
        description: 'Kitchen item deleted successfully',
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: error?.message || 'Failed to delete kitchen item',
        variant: 'destructive',
      });
    },
  });

  const onSubmit = (data: KitchenItemFormData) => {
    if (editingItem) {
      updateMutation.mutate({ id: editingItem.id, data });
    } else {
      createMutation.mutate(data);
    }
  };

  // Generate suggested PO number
  const generateSuggestedPONumber = () => {
    const today = new Date();
    const dateStr = format(today, 'yyyyMMdd');
    const count = (kitchenItems.length + 1).toString().padStart(3, '0');
    return `KIT-${dateStr}-${count}`;
  };

  const handleEdit = (item: KitchenItem) => {
    setEditingItem(item);
    form.reset({
      poNumber: item.poNumber,
      description: item.description,
      amount: item.amount,
      date: new Date(item.date).toISOString().split('T')[0],
      status: item.status || 'in_stock',
    });
    setIsCreateOpen(true);
  };

  const handleDelete = async (id: string) => {
    if (window.confirm(tSync('Are you sure you want to delete this kitchen item?'))) {
      deleteMutation.mutate(id);
    }
  };

  // Generate CSV report
  const generateCSVReport = () => {
    const filteredItems = kitchenItems.filter(item => {
      const itemDate = new Date(item.date);
      const start = new Date(reportStartDate);
      const end = new Date(reportEndDate);
      return itemDate >= start && itemDate <= end;
    });

    if (filteredItems.length === 0) {
      toast({
        title: 'No Data',
        description: 'No kitchen items found in the selected date range',
        variant: 'destructive',
      });
      return;
    }

    // Create CSV content
    const headers = ['PO Number', 'Date', 'Description', 'Amount'];
    const csvContent = [
      headers.join(','),
      ...filteredItems.map(item => [
        `"${item.poNumber}"`,
        format(new Date(item.date), 'yyyy-MM-dd'),
        `"${item.description.replace(/"/g, '""')}"`,
        Number(item.amount).toFixed(2)
      ].join(','))
    ].join('\n');

    // Add summary row
    const totalAmount = filteredItems.reduce((sum, item) => sum + Number(item.amount), 0);
    const summaryRow = `\n\nTotal Items,${filteredItems.length},,${totalAmount.toFixed(2)}`;
    const finalContent = csvContent + summaryRow;

    // Download CSV
    const blob = new Blob([finalContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `kitchen-items-report-${reportStartDate}-to-${reportEndDate}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    toast({
      title: 'Success',
      description: 'CSV report downloaded successfully',
    });
  };

  // Generate PDF report
  const generatePDFReport = () => {
    const filteredItems = kitchenItems.filter(item => {
      const itemDate = new Date(item.date);
      const start = new Date(reportStartDate);
      const end = new Date(reportEndDate);
      return itemDate >= start && itemDate <= end;
    });

    if (filteredItems.length === 0) {
      toast({
        title: 'No Data',
        description: 'No kitchen items found in the selected date range',
        variant: 'destructive',
      });
      return;
    }

    // Create HTML content for PDF
    const totalAmount = filteredItems.reduce((sum, item) => sum + Number(item.amount), 0);
    const htmlContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8">
        <title>Kitchen Items Report</title>
        <style>
          body { font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; padding: 30px; background-color: #ffffff; }
          h1 { color: #1a1a1a; margin: 0; font-size: 28px; font-weight: 600; background: #c29f72; padding: 20px; border-radius: 4px; margin-bottom: 20px; border-bottom: 2px solid #a88a5f; }
          .info { margin: 20px 0; color: #333; background: #f9f5f0; padding: 15px; border-radius: 4px; border-left: 4px solid #c29f72; }
          table { width: 100%; border-collapse: collapse; margin: 20px 0; background: white; border: 1px solid #ddd; }
          th { background: #c29f72; color: #1a1a1a; padding: 12px; text-align: left; font-weight: 600; font-size: 13px; border-bottom: 2px solid #a88a5f; }
          td { padding: 10px 12px; border-bottom: 1px solid #e5e5e5; color: #333; }
          tr:nth-child(even) { background-color: #fafafa; }
          .summary { margin-top: 20px; padding: 18px; background: #c29f72; border-radius: 4px; border: 2px solid #a88a5f; }
          .summary-row { display: flex; justify-content: space-between; margin: 6px 0; font-size: 15px; }
          .summary-label { font-weight: 600; color: #1a1a1a; }
          .summary-value { color: #1a1a1a; font-weight: 700; font-size: 16px; }
        </style>
      </head>
      <body>
        <h1>Kitchen Items Report</h1>
        <div class="info">
          <p><strong>Report Period:</strong> ${format(new Date(reportStartDate), 'MMM dd, yyyy')} - ${format(new Date(reportEndDate), 'MMM dd, yyyy')}</p>
          <p><strong>Generated:</strong> ${format(new Date(), 'MMM dd, yyyy HH:mm')}</p>
        </div>
        <table>
          <thead>
            <tr>
              <th>PO Number</th>
              <th>Date</th>
              <th>Description</th>
              <th style="text-align: right;">Amount</th>
            </tr>
          </thead>
          <tbody>
            ${filteredItems.map(item => `
              <tr>
                <td>${item.poNumber}</td>
                <td>${format(new Date(item.date), 'MMM dd, yyyy')}</td>
                <td>${item.description}</td>
                <td style="text-align: right;">${formatCurrency(item.amount)}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>
        <div class="summary">
          <div class="summary-row">
            <span class="summary-label">Total Items:</span>
            <span class="summary-value">${filteredItems.length}</span>
          </div>
          <div class="summary-row">
            <span class="summary-label">Total Amount:</span>
            <span class="summary-value">${formatCurrency(totalAmount)}</span>
          </div>
        </div>
      </body>
      </html>
    `;

    // Open print dialog
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(htmlContent);
      printWindow.document.close();
      printWindow.focus();
      setTimeout(() => {
        printWindow.print();
      }, 250);
    }

    toast({
      title: 'Success',
      description: 'PDF report opened for printing/saving',
    });
  };

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="border-blue-200 bg-blue-50">
          <CardContent className="pt-6">
            <div className="text-sm text-blue-600 mb-1">Total Items</div>
            <div className="text-3xl font-bold text-blue-700">{pagination?.total || kitchenItems.length}</div>
          </CardContent>
        </Card>
        
        <Card className="border-green-200 bg-green-50">
          <CardContent className="pt-6">
            <div className="text-sm text-green-600 mb-1">Total Amount</div>
            <div className="text-3xl font-bold text-green-700">
              {formatCurrency(kitchenItems.reduce((sum, item) => sum + Number(item.amount || 0), 0))}
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-purple-200 bg-purple-50">
          <CardContent className="pt-6">
            <div className="text-sm text-purple-600 mb-1">Average Amount</div>
            <div className="text-3xl font-bold text-purple-700">
              {formatCurrency(
                kitchenItems.length > 0 
                  ? kitchenItems.reduce((sum, item) => sum + Number(item.amount || 0), 0) / kitchenItems.length
                  : 0
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold"><TranslatedText text="Kitchen Items" /></h2>
          <p className="text-muted-foreground"><TranslatedText text="Manage kitchen stock and supplies" /></p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => setIsReportOpen(true)}
          >
            <Download className="w-4 h-4 mr-2" />
            <TranslatedText text="Review Report" />
          </Button>
          <Button
            onClick={() => {
              setEditingItem(null);
              form.reset({
                poNumber: generateSuggestedPONumber(),
                description: '',
                amount: 0,
                date: new Date().toISOString().split('T')[0],
                status: 'in_stock',
              });
              setIsCreateOpen(true);
            }}
          >
            <Plus className="w-4 h-4 mr-2" />
            <TranslatedText text="Add Kitchen Item" />
          </Button>
        </div>
      </div>

      {/* Kitchen Items Table */}
      <Card>
        <CardHeader>
          <CardTitle><TranslatedText text="Kitchen Stock" /></CardTitle>
        </CardHeader>
        <CardContent>
          {/* Search */}
          <div className="mb-6">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <Input
                placeholder={tSync("Search by PO number or description...")}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>

          {/* Table */}
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <MoonLoader size="lg" />
            </div>
          ) : (
            <div className="overflow-hidden">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {kitchenItems.length === 0 ? (
                    <div className="col-span-full text-center py-8">
                      <div className="text-gray-500">
                        <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
                        <p><TranslatedText text="No kitchen items found" /></p>
                        <p className="text-sm"><TranslatedText text="Add your first kitchen item to get started" /></p>
                      </div>
                    </div>
                  ) : (
                    kitchenItems.map((item) => (
                      <Card key={item.id} className="overflow-hidden hover:shadow-lg transition-shadow border-2 border-gray-200">
                        <CardHeader className="bg-gray-50/50 pb-4 border-b border-gray-200">
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <CardTitle className="text-base font-semibold text-gray-900">
                                  {item.poNumber}
                                </CardTitle>
                                {item.status && (
                                  <span
                                    className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                                      item.status === 'in_stock'
                                        ? 'bg-green-100 text-green-800'
                                        : 'bg-red-100 text-red-800'
                                    }`}
                                    title={item.status === 'in_stock' ? 'In Stock' : 'Out of Stock'}
                                  >
                                    {item.status === 'in_stock' ? (
                                      <Package className="w-3 h-3" />
                                    ) : (
                                      <PackageX className="w-3 h-3" />
                                    )}
                                    {item.status === 'in_stock' ? 'In Stock' : 'Out of Stock'}
                                  </span>
                                )}
                              </div>
                              <p className="text-sm text-gray-500 mt-1">
                                {format(new Date(item.date), 'MMM dd, yyyy')}
                              </p>
                            </div>
                            <div className="flex gap-1">
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 hover:bg-blue-100 hover:text-blue-700"
                                onClick={() => handleEdit(item)}
                              >
                                <Edit className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 hover:bg-red-100 hover:text-red-700"
                                onClick={() => handleDelete(item.id)}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        </CardHeader>
                        <CardContent className="pt-4">
                          <div className="space-y-4">
                            <div>
                              <p className="text-sm font-medium text-gray-500 mb-1">
                                <TranslatedText text="Description" />
                              </p>
                              <p className="text-sm text-gray-900 line-clamp-3 min-h-[3rem]">
                                {item.description}
                              </p>
                            </div>
                            <div className="pt-4 border-t border-gray-100 flex justify-between items-end">
                              <div>
                                <p className="text-sm font-medium text-gray-500">
                                  <TranslatedText text="Amount" />
                                </p>
                              </div>
                              <p className="text-xl font-bold text-gray-900">
                                {formatCurrency(item.amount)}
                              </p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))
                  )}
                </div>

              {/* Pagination */}
              {pagination && (
                <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mt-6 pt-4 border-t border-gray-200">
                  <div className="flex items-center gap-4">
                    <div className="text-sm text-gray-600">
                      Showing {pagination.totalCount === 0 ? 0 : ((pagination.page - 1) * pagination.pageSize) + 1} to {Math.min(pagination.page * pagination.pageSize, pagination.totalCount)} of {pagination.totalCount} items
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-600">Items per page:</span>
                      <Select
                        value={pageSize.toString()}
                        onValueChange={(value) => {
                          setPageSize(Number(value));
                          setCurrentPage(1);
                        }}
                      >
                        <SelectTrigger className="w-20 h-8">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="5">5</SelectItem>
                          <SelectItem value="10">10</SelectItem>
                          <SelectItem value="20">20</SelectItem>
                          <SelectItem value="50">50</SelectItem>
                          <SelectItem value="100">100</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="text-sm text-gray-600">
                      Page {pagination.page} of {pagination.totalPages}
                    </div>
                    <div className="flex gap-1">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCurrentPage(1)}
                        disabled={!pagination.hasPrev}
                      >
                        First
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                        disabled={!pagination.hasPrev}
                      >
                        Previous
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCurrentPage(p => p + 1)}
                        disabled={!pagination.hasNext}
                      >
                        Next
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCurrentPage(pagination.totalPages)}
                        disabled={!pagination.hasNext}
                      >
                        Last
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {editingItem ? <TranslatedText text="Edit Kitchen Item" /> : <TranslatedText text="Add Kitchen Item" />}
            </DialogTitle>
            <DialogDescription>
              {editingItem
                ? <TranslatedText text="Update the kitchen item details" />
                : <TranslatedText text="Add a new kitchen stock item" />}
            </DialogDescription>
          </DialogHeader>

          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <FormField
                control={form.control}
                name="poNumber"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel><TranslatedText text="PO Number" /></FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="KIT-001" className="border-2 border-gray-300 focus:border-blue-500" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel><TranslatedText text="Description" /></FormLabel>
                    <FormControl>
                      <Textarea {...field} placeholder="Fresh vegetables for restaurant" rows={3} className="border-2 border-gray-300 focus:border-blue-500" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="amount"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel><TranslatedText text="Amount" /></FormLabel>
                    <FormControl>
                      <Input {...field} type="number" step="0.01" placeholder="0.00" className="border-2 border-gray-300 focus:border-blue-500" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="date"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel><TranslatedText text="Date" /></FormLabel>
                    <FormControl>
                      <Input {...field} type="date" className="border-2 border-gray-300 focus:border-blue-500" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="status"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel><TranslatedText text="Status" /></FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger className="w-full border-2 border-gray-300 focus:border-blue-500">
                          <SelectValue placeholder="Select status" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="in_stock">
                          <div className="flex items-center gap-2">
                            <Package className="w-4 h-4 text-green-600" />
                            <span>In Stock</span>
                          </div>
                        </SelectItem>
                        <SelectItem value="out_of_stock">
                          <div className="flex items-center gap-2">
                            <PackageX className="w-4 h-4 text-red-600" />
                            <span>Out of Stock</span>
                          </div>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="flex justify-end gap-2 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setIsCreateOpen(false);
                    setEditingItem(null);
                    form.reset();
                  }}
                >
                  <TranslatedText text="Cancel" />
                </Button>
                <Button type="submit" disabled={createMutation.isPending || updateMutation.isPending}>
                  {createMutation.isPending || updateMutation.isPending ? (
                    <MoonLoader size="sm" />
                  ) : editingItem ? (
                    <TranslatedText text="Update" />
                  ) : (
                    <TranslatedText text="Create" />
                  )}
                </Button>
              </div>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      {/* Report Dialog */}
      <Dialog open={isReportOpen} onOpenChange={setIsReportOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              <TranslatedText text="Kitchen Items Report" />
            </DialogTitle>
            <DialogDescription>
              <TranslatedText text="Select a date range to preview and download the report" />
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Date Range Selection */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">
                  <TranslatedText text="Start Date" />
                </label>
                <Input
                  type="date"
                  value={reportStartDate}
                  onChange={(e) => setReportStartDate(e.target.value)}
                  className="border-2 border-gray-300 focus:border-blue-500"
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">
                  <TranslatedText text="End Date" />
                </label>
                <Input
                  type="date"
                  value={reportEndDate}
                  onChange={(e) => setReportEndDate(e.target.value)}
                  className="border-2 border-gray-300 focus:border-blue-500"
                />
              </div>
            </div>

            {/* Report Preview */}
            <div className="border-2 border-gray-200 rounded-lg p-4 bg-gray-50">
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <FileText className="w-5 h-5" />
                <TranslatedText text="Report Preview" />
              </h3>

              {(() => {
                const filteredItems = kitchenItems.filter(item => {
                  const itemDate = new Date(item.date);
                  const start = new Date(reportStartDate);
                  const end = new Date(reportEndDate);
                  return itemDate >= start && itemDate <= end;
                });

                const totalAmount = filteredItems.reduce((sum, item) => sum + Number(item.amount), 0);

                if (filteredItems.length === 0) {
                  return (
                    <div className="text-center py-8 text-gray-500">
                      <FileText className="w-12 h-12 mx-auto mb-2 opacity-50" />
                      <p><TranslatedText text="No items found in the selected date range" /></p>
                    </div>
                  );
                }

                return (
                  <>
                    {/* Summary Cards */}
                    <div className="grid grid-cols-3 gap-4 mb-4">
                      <Card className="border-2 border-blue-200 bg-blue-50">
                        <CardContent className="pt-4">
                          <div className="text-sm text-gray-600 mb-1">Total Items</div>
                          <div className="text-2xl font-bold text-blue-700">{filteredItems.length}</div>
                        </CardContent>
                      </Card>
                      <Card className="border-2 border-green-200 bg-green-50">
                        <CardContent className="pt-4">
                          <div className="text-sm text-gray-600 mb-1">Total Amount</div>
                          <div className="text-2xl font-bold text-green-700">{formatCurrency(totalAmount)}</div>
                        </CardContent>
                      </Card>
                      <Card className="border-2 border-purple-200 bg-purple-50">
                        <CardContent className="pt-4">
                          <div className="text-sm text-gray-600 mb-1">Average Amount</div>
                          <div className="text-2xl font-bold text-purple-700">
                            {formatCurrency(totalAmount / filteredItems.length)}
                          </div>
                        </CardContent>
                      </Card>
                    </div>

                    {/* Items Table Preview */}
                    <div className="border border-gray-300 rounded-lg overflow-hidden">
                      <div className="max-h-64 overflow-y-auto">
                        <table className="w-full">
                          <thead className="bg-gray-200 sticky top-0">
                            <tr>
                              <th className="px-4 py-2 text-left text-sm font-semibold">PO Number</th>
                              <th className="px-4 py-2 text-left text-sm font-semibold">Date</th>
                              <th className="px-4 py-2 text-left text-sm font-semibold">Description</th>
                              <th className="px-4 py-2 text-right text-sm font-semibold">Amount</th>
                            </tr>
                          </thead>
                          <tbody className="bg-white">
                            {filteredItems.map((item, index) => (
                              <tr key={item.id} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                                <td className="px-4 py-2 text-sm font-medium">{item.poNumber}</td>
                                <td className="px-4 py-2 text-sm">{format(new Date(item.date), 'MMM dd, yyyy')}</td>
                                <td className="px-4 py-2 text-sm truncate max-w-xs" title={item.description}>
                                  {item.description}
                                </td>
                                <td className="px-4 py-2 text-sm text-right font-medium">
                                  {formatCurrency(item.amount)}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                          <tfoot className="bg-gray-100 border-t-2 border-gray-300">
                            <tr>
                              <td colSpan={3} className="px-4 py-2 text-sm font-bold">Total</td>
                              <td className="px-4 py-2 text-sm text-right font-bold">
                                {formatCurrency(totalAmount)}
                              </td>
                            </tr>
                          </tfoot>
                        </table>
                      </div>
                    </div>
                  </>
                );
              })()}
            </div>

            {/* Export Buttons */}
            <div className="pt-4 border-t">
              <p className="text-sm text-gray-600 mb-4">
                <TranslatedText text="Download report in your preferred format:" />
              </p>
              <div className="grid grid-cols-2 gap-3">
                <Button
                  variant="outline"
                  className="h-20 flex-col gap-2 text-gray-700 hover:bg-green-50 hover:border-green-500 hover:text-green-700"
                  onClick={() => {
                    generateCSVReport();
                    setIsReportOpen(false);
                  }}
                  disabled={kitchenItems.filter(item => {
                    const itemDate = new Date(item.date);
                    const start = new Date(reportStartDate);
                    const end = new Date(reportEndDate);
                    return itemDate >= start && itemDate <= end;
                  }).length === 0}
                  title="Download report as CSV file for use in spreadsheet applications"
                >
                  <FileSpreadsheet className="w-6 h-6 text-green-600 group-hover:text-green-700" />
                  <span className="text-sm font-medium">Export to CSV</span>
                </Button>
                <Button
                  variant="outline"
                  className="h-20 flex-col gap-2 text-gray-700 hover:bg-red-50 hover:border-red-500 hover:text-red-700"
                  onClick={() => {
                    generatePDFReport();
                    setIsReportOpen(false);
                  }}
                  disabled={kitchenItems.filter(item => {
                    const itemDate = new Date(item.date);
                    const start = new Date(reportStartDate);
                    const end = new Date(reportEndDate);
                    return itemDate >= start && itemDate <= end;
                  }).length === 0}
                  title="Download report as PDF file for printing or sharing"
                >
                  <FileDown className="w-6 h-6 text-red-600 group-hover:text-red-700" />
                  <span className="text-sm font-medium">Export to PDF</span>
                </Button>
              </div>
            </div>
          </div>

          <div className="flex justify-end border-t pt-4">
            <Button
              variant="outline"
              onClick={() => setIsReportOpen(false)}
            >
              <TranslatedText text="Cancel" />
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
